import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  collection, getDocs, addDoc, setDoc, deleteDoc,
  doc, query, orderBy, serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase/firebase-config";
import imgKnowMe      from "../assets/images/KnowMoreAboutMe.jpeg";
import imgSkills      from "../assets/images/Skills.jpeg";
import imgJourney     from "../assets/images/Journey.jpeg";
import imgLetsConnect from "../assets/images/LetsConnect.jpeg";

/* Seed data — mirrors the hardcoded chapters in JourneySection */
const SEED_CHAPTERS = [
  {
    order: 1, tag: "THE SPARK", year: "2016",
    title: "Where It All Began",
    story: "I was born in Odisha and raised between Bhubaneswar and Jamshedpur, before heading to Delhi for my B.Com. While commerce made sense, it was law that sparked something deeper. I discovered that while numbers can add up, it's words that can move people.",
    quote: "Numbers added up — but words moved people.",
    detail: "Odisha → Delhi → Law",
    visaLabel: "ENTRY GRANTED",
    icon: "mapmarker",
    accent: "#C25B2A", stampColor: "#C25B2A",
    imgPos: "center top", flip: false, isFinal: false,
    localPhoto: imgKnowMe,
  },
  {
    order: 2, tag: "THE BUILD", year: "2017–19",
    title: "Law, Leadership & Leap",
    story: "From standing first in my law school batch to leading student councils and winning scholarships. I dove deep into the legal world — and loved it: I gained real-world experience in top-Indian firms like Wipro and WNS Denali, managing contracts, ensuring compliance, and learning what it means to solve problems before they happen.",
    quote: "I learned to solve problems before they happen.",
    detail: "Wipro · WNS Denali · Scholar",
    visaLabel: "MERIT VERIFIED",
    icon: "gavel",
    accent: "#b88822", stampColor: "#b88822",
    imgPos: "center center", flip: true, isFinal: false,
    localPhoto: imgSkills,
  },
  {
    order: 3, tag: "THE PAUSE", year: "2020–22",
    title: "The Dream, Delayed – Not Denied",
    story: "2020 was supposed to be my London chapter — my LLM at Queen Mary. But the world shut down. I stayed back, kept working, and when the time was right, I tried again. In 2022, I landed in London — two years late, but with double the passion. I completed my LLM with Distinction while working part-time roles and building a life from scratch.",
    quote: "When the window reopened, I flew.",
    detail: "Queen Mary · London · Resilience",
    visaLabel: "RESILIENCE",
    icon: "hourglass",
    accent: "#C25B2A", stampColor: "#8B4513",
    imgPos: "center top", flip: false, isFinal: false,
    localPhoto: imgJourney,
  },
  {
    order: 4, tag: "THE SHIFT", year: "2024+",
    title: "The Search, The Shift",
    story: "Post-LLM, I dove into the job hunt — CVs, interviews, rejection emails. I had the experience, the degree, the drive — but something still wasn't clicking. That's when I stopped chasing and started building. This website is my shift — from applicant to author of my own narrative.",
    quote: "I stopped chasing and started building.",
    detail: "Applicant → Author · Now",
    visaLabel: "AUTHOR MODE",
    icon: "search",
    accent: "#D4AF37", stampColor: "#D4AF37",
    imgPos: "center center", flip: true, isFinal: true,
    localPhoto: imgLetsConnect,
  },
];

const ICON_OPTIONS = [
  { value: "mapmarker",   label: "📍 Map Marker" },
  { value: "gavel",       label: "⚖ Gavel / Law" },
  { value: "hourglass",   label: "⏳ Hourglass" },
  { value: "search",      label: "🔍 Search" },
  { value: "plane",       label: "✈ Plane" },
  { value: "graduation",  label: "🎓 Graduation" },
  { value: "briefcase",   label: "💼 Briefcase" },
  { value: "rocket",      label: "🚀 Rocket" },
  { value: "star",        label: "⭐ Star" },
  { value: "lightbulb",   label: "💡 Lightbulb" },
  { value: "trophy",      label: "🏆 Trophy" },
  { value: "heart",       label: "♥ Heart" },
  { value: "globe",       label: "🌐 Globe" },
  { value: "book",        label: "📖 Book" },
];

const BLANK = {
  order: 5,
  tag: "",
  year: "",
  title: "",
  story: "",
  quote: "",
  detail: "",
  visaLabel: "",
  accent: "#c25b2a",
  stampColor: "#c25b2a",
  icon: "mapmarker",
  photo: "",
  imgPos: "center center",
  flip: false,
  isFinal: false,
};

/* ── PHASE MODAL ── */
const PhaseModal = ({ phase, onSave, onClose }) => {
  const [form, setForm] = useState(phase ? { ...BLANK, ...phase } : { ...BLANK });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [error, setError] = useState("");
  const fileRef = useRef();

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleUpload = e => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadPct(0);
    const storageRef = ref(storage, `journey/${Date.now()}_${file.name}`);
    const task = uploadBytesResumable(storageRef, file);
    task.on("state_changed",
      snap => setUploadPct(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      () => { setUploading(false); setError("Upload failed."); },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        set("photo", url);
        setUploading(false);
      }
    );
  };

  const handleSave = async () => {
    if (!form.tag.trim() || !form.title.trim()) { setError("Tag and Title are required."); return; }
    setSaving(true);
    setError("");
    try {
      await onSave(form);
      onClose();
    } catch (e) {
      setError("Save failed. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="adm-journey-modal-overlay" onClick={onClose}>
      <div className="adm-journey-modal" onClick={e => e.stopPropagation()}>
        <div className="adm-journey-modal-head">
          <h3>{phase ? "Edit Phase" : "New Journey Phase"}</h3>
          <button className="adm-journey-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="adm-journey-modal-body">
          {/* Row 1: Order + Tag + Year */}
          <div className="adm-journey-row3">
            <div className="adm-field">
              <label>Order <em>(lower = first)</em></label>
              <input type="number" min={1} value={form.order}
                onChange={e => set("order", Number(e.target.value))} />
            </div>
            <div className="adm-field">
              <label>Tag <em>e.g. THE SPARK</em></label>
              <input value={form.tag} onChange={e => set("tag", e.target.value.toUpperCase())} placeholder="THE SPARK" />
            </div>
            <div className="adm-field">
              <label>Year / Period</label>
              <input value={form.year} onChange={e => set("year", e.target.value)} placeholder="2016 or 2017–19" />
            </div>
          </div>

          {/* Title */}
          <div className="adm-field">
            <label>Chapter Title</label>
            <input value={form.title} onChange={e => set("title", e.target.value)} placeholder="Where It All Began" />
          </div>

          {/* Story */}
          <div className="adm-field">
            <label>Story <em>(the main narrative)</em></label>
            <textarea rows={5} value={form.story}
              onChange={e => set("story", e.target.value)}
              placeholder="Write the story of this phase of your journey…" />
          </div>

          {/* Quote + Detail */}
          <div className="adm-journey-row2">
            <div className="adm-field">
              <label>Pull Quote</label>
              <input value={form.quote} onChange={e => set("quote", e.target.value)}
                placeholder="Numbers added up — but words moved people." />
            </div>
            <div className="adm-field">
              <label>Map stop (place line)</label>
              <input value={form.detail} onChange={e => set("detail", e.target.value)}
                placeholder="Odisha → Delhi → Law" />
            </div>
          </div>

          {/* Visa Label + Icon */}
          <div className="adm-journey-row2">
            <div className="adm-field">
              <label>Passport Stamp Text</label>
              <input value={form.visaLabel} onChange={e => set("visaLabel", e.target.value.toUpperCase())}
                placeholder="ENTRY GRANTED" />
            </div>
            <div className="adm-field">
              <label>Icon</label>
              <select value={form.icon} onChange={e => set("icon", e.target.value)}>
                {ICON_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Colors */}
          <div className="adm-journey-row3">
            <div className="adm-field">
              <label>Accent Color</label>
              <div className="adm-color-row">
                <input type="color" value={form.accent} onChange={e => set("accent", e.target.value)} />
                <input type="text" value={form.accent} onChange={e => set("accent", e.target.value)} />
              </div>
            </div>
            <div className="adm-field">
              <label>Stamp Color</label>
              <div className="adm-color-row">
                <input type="color" value={form.stampColor} onChange={e => set("stampColor", e.target.value)} />
                <input type="text" value={form.stampColor} onChange={e => set("stampColor", e.target.value)} />
              </div>
            </div>
            <div className="adm-field">
              <label>Image Position</label>
              <input value={form.imgPos} onChange={e => set("imgPos", e.target.value)}
                placeholder="center top" />
            </div>
          </div>

          {/* Photo */}
          <div className="adm-field">
            <label>Phase Photo</label>
            <div className="adm-journey-photo-row">
              {form.photo && (
                <img src={form.photo} alt="preview" className="adm-journey-photo-preview" />
              )}
              <div className="adm-journey-photo-inputs">
                <input type="url" value={form.photo} onChange={e => set("photo", e.target.value)}
                  placeholder="https://… or upload below" />
                <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
                  onChange={handleUpload} />
                <button type="button" className="adm-btn-edit" onClick={() => fileRef.current.click()}
                  disabled={uploading}>
                  {uploading ? `Uploading ${uploadPct}%…` : "Upload image"}
                </button>
              </div>
            </div>
          </div>

          {/* Toggles */}
          <div className="adm-journey-toggles">
            <label className="adm-toggle-row">
              <div className="adm-toggle-wrap">
                <input type="checkbox" checked={form.flip} onChange={e => set("flip", e.target.checked)} />
                <span className="adm-toggle" />
              </div>
              <span>Flip layout (photo on left)</span>
            </label>
            <label className="adm-toggle-row">
              <div className="adm-toggle-wrap">
                <input type="checkbox" checked={form.isFinal} onChange={e => set("isFinal", e.target.checked)} />
                <span className="adm-toggle" />
              </div>
              <span>Final chapter (shows "continues…" ending)</span>
            </label>
          </div>

          {error && <p className="adm-journey-error">{error}</p>}
        </div>

        <div className="adm-journey-modal-foot">
          <button className="adm-draft-btn" onClick={onClose}>Cancel</button>
          <button className="adm-publish-btn" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save Phase"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── JOURNEY PANEL (shown inside Dashboard) ── */
const JourneyPanel = () => {
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | "new" | phase object
  const [deleting, setDeleting] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [seedProgress, setSeedProgress] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, "journey_phases"), orderBy("order", "asc")));
      setPhases(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (_) {
      setPhases([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /* Upload a local image import (data URL or blob URL) to Firebase Storage */
  const uploadLocalImage = async (imgSrc, filename) => {
    const response = await fetch(imgSrc);
    const blob = await response.blob();
    const storageRef = ref(storage, `journey/${filename}`);
    return new Promise((resolve, reject) => {
      const task = uploadBytesResumable(storageRef, blob);
      task.on("state_changed", null, reject, async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        resolve(url);
      });
    });
  };

  const handleSeed = async () => {
    if (!window.confirm("This will push the 4 default journey chapters (with photos) to Firebase. Continue?")) return;
    setSeeding(true);
    try {
      for (let i = 0; i < SEED_CHAPTERS.length; i++) {
        const ch = SEED_CHAPTERS[i];
        setSeedProgress(`Uploading photo ${i + 1} of ${SEED_CHAPTERS.length}…`);
        const photoUrl = await uploadLocalImage(ch.localPhoto, `0${ch.order}-${ch.tag.toLowerCase().replace(/\s+/g, "-")}.jpeg`);
        setSeedProgress(`Saving phase ${i + 1} of ${SEED_CHAPTERS.length}…`);
        const { localPhoto, ...data } = ch;
        await addDoc(collection(db, "journey_phases"), {
          ...data,
          photo: photoUrl,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      setSeedProgress("Done!");
      await load();
    } catch (e) {
      setSeedProgress("Error: " + e.message);
    } finally {
      setSeeding(false);
      setTimeout(() => setSeedProgress(""), 3000);
    }
  };

  const handleSave = async (form) => {
    const { id, ...data } = form;
    const payload = {
      ...data,
      order: Number(data.order),
      updatedAt: serverTimestamp(),
    };
    if (id) {
      await setDoc(doc(db, "journey_phases", id), payload, { merge: true });
    } else {
      payload.createdAt = serverTimestamp();
      await addDoc(collection(db, "journey_phases"), payload);
    }
    await load();
  };

  const handleDelete = async (phase) => {
    if (!window.confirm(`Delete phase "${phase.title}"? This cannot be undone.`)) return;
    setDeleting(phase.id);
    try {
      await deleteDoc(doc(db, "journey_phases", phase.id));
      await load();
    } finally {
      setDeleting(null);
    }
  };

  return (
    <section className="adm-panel adm-journey-panel">
      <div className="adm-panel-head">
        <div>
          <h2>Journey Phases</h2>
          <p>Your story — add, edit, reorder or delete chapters of your journey</p>
        </div>
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <span className="adm-panel-count">{phases.length} phases</span>
          <button className="adm-new-blog" onClick={() => setModal("new")}>+ Add Phase</button>
        </div>
      </div>

      {loading ? (
        <div className="adm-dash-loading" style={{ padding: "1.5rem" }}>
          <div className="adm-spinner" />
        </div>
      ) : phases.length === 0 ? (
        <div className="adm-empty">
          <span>—</span>
          <p>No journey phases yet. Your 4 existing chapters are shown by default on the live page.</p>
          <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center", flexWrap: "wrap", marginTop: "0.5rem" }}>
            <button
              className="adm-publish-btn"
              onClick={handleSeed}
              disabled={seeding}
              style={{ fontSize: "0.9rem" }}
            >
              {seeding ? seedProgress || "Seeding…" : "Push existing 4 chapters to Firebase"}
            </button>
            <button onClick={() => setModal("new")} style={{ fontSize: "0.9rem" }}>+ Add manually</button>
          </div>
          {seedProgress && <p style={{ color: "#c25b2a", fontSize: "0.8rem", marginTop: "0.5rem" }}>{seedProgress}</p>}
        </div>
      ) : (
        <div className="adm-journey-list">
          {phases.map(p => (
            <div key={p.id} className="adm-journey-row">
              <div className="adm-journey-row-order">{String(p.order).padStart(2, "0")}</div>
              {p.photo && (
                <div
                  className="adm-journey-row-thumb"
                  style={{ backgroundImage: `url(${p.photo})` }}
                />
              )}
              <div className="adm-journey-row-info">
                <span className="adm-journey-tag">{p.tag}</span>
                <strong className="adm-journey-title">{p.title}</strong>
                <span className="adm-journey-year">{p.year}</span>
                {p.story && (
                  <p className="adm-journey-excerpt">
                    {p.story.slice(0, 100)}{p.story.length > 100 ? "…" : ""}
                  </p>
                )}
              </div>
              <div className="adm-row-actions">
                <button className="adm-btn-edit" onClick={() => setModal(p)}>Edit</button>
                <button
                  className="adm-btn-del"
                  onClick={() => handleDelete(p)}
                  disabled={deleting === p.id}
                >
                  {deleting === p.id ? "…" : "Delete"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <PhaseModal
          phase={modal === "new" ? null : modal}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
    </section>
  );
};

export default JourneyPanel;
